import { useEffect, useState } from "react";

// Kutunun altında bırakılacak pay ve inebileceği en küçük yükseklik. Ayar olarak dışarı
// açmıyorum: iki çağıranın ikisi de aynı değeri istiyor ve geçilmeyen bir parametre, okuyana
// "bir yerde başka bir değer var" diye aratmaktan başka işe yaramıyordu.
const BOTTOM_GAP = 8;
const MIN_HEIGHT = 240;

// Elemanın altında, kapsayıcılarının içinde kalan yer: sayfalama şeridi, kart iç boşlukları,
// sayfa payı. Bunu ölçmezsem liste ekranın tam altına kadar uzuyor ve sayfalama görünmez oluyor —
// düzeltmeye çalıştığımız şeyin aynısı, bir satır aşağıda.
//
// Kapsayıcının alt kenarına DEĞİL, altındaki kardeşlere bakılıyor: AppShell.Main gibi ekran boyu
// gerilen kutularda alt kenar çok aşağıda kalıyor ve o boşluk "altta dolu yer" sanılıyordu; liste
// dört satıra çöküyordu.
//
// Liste kısalınca kapsayıcılar da kısalıyor ama bu mesafe DEĞİŞMİYOR (altta duran şeylerin kendi
// yüksekliği), o yüzden ölçüm kendi kendini kovalamıyor.
function spaceBelow(element) {
  let total = 0;
  let current = element;

  while (current.parentElement && current.parentElement !== document.body) {
    const parent = current.parentElement;
    const bottom = current.getBoundingClientRect().bottom;
    let occupied = bottom;

    for (
      let sibling = current.nextElementSibling;
      sibling;
      sibling = sibling.nextElementSibling
    ) {
      occupied = Math.max(occupied, sibling.getBoundingClientRect().bottom);
    }

    total +=
      Math.max(0, occupied - bottom) +
      (parseFloat(getComputedStyle(parent).paddingBottom) || 0);
    current = parent;
  }

  return total;
}

// Yüksekliği TAM SATIRA yuvarlar. Yuvarlamadan kutu satırın ortasından kesiliyor ve alt kenarda
// yarım bir satır kalıyor.
function snapToRow(element, available, rowSelector, headerSelector) {
  const row = element.querySelector(rowSelector);
  const step = row?.getBoundingClientRect().height ?? 0;
  // Yapışkan başlık ve yatay kaydırma çubuğu satırlara kalan yerin dışında. Çubuk hesaba
  // katılmazsa kendi yüksekliği kadar aşağıyı yiyor ve son satır yine ortadan kesiliyor.
  // Mantine'in ScrollArea'sı içerik üstüne binen çubuk kullandığı için orada bu fark sıfır.
  const gutter = element.offsetHeight - element.clientHeight;
  const offset =
    (element.querySelector(headerSelector)?.getBoundingClientRect().height ??
      0) + gutter;

  // İskelet satırları ve boş liste durumu farklı yükseklikte; ölçülemeyecek kadar küçükse
  // yuvarlamayı hiç uygulamıyorum, yoksa liste bir satıra çökerdi.
  if (step < 8 || available < offset + step * 2) {
    return available;
  }

  return offset + Math.floor((available - offset) / step) * step;
}

// Kaydırma kutusunun ekranın ALT KENARINA kadar uzamasını, daha fazlasına değil, sağlıyor.
//
// Sabit bir `calc(100vh - 420px)` yazmadım. Üstte ve altta kalan yükseklik ekrandan ekrana
// değişiyor — mobilde ayrıca bir başlık çubuğu var, süper admin gezerken uyarı şeridi ekleniyor,
// özet kartları dar ekranda alt alta iniyor. Sabit sayı bir monitörde doğru, diğerinde yanlış
// olurdu; zaten "her cihazda yeni bir düzeltme" döngüsünün sebebi de bu.
export function useFitViewport({ rowSelector, headerSelector }) {
  const [node, setNode] = useState(null);
  const [maxHeight, setMaxHeight] = useState(undefined);

  useEffect(() => {
    if (!node) {
      return;
    }

    let frame = 0;

    const measure = () => {
      frame = 0;

      const available =
        window.innerHeight -
        node.getBoundingClientRect().top -
        spaceBelow(node) -
        BOTTOM_GAP;
      // Alt sınır yuvarlamadan ÖNCE uygulanıyor: sonra uygulasaydım küçük pencerede sonucu
      // ezip yarım satır geri getirirdi. Yuvarlama da şart — alt piksel farkları
      // ölçüm→yeniden çizim→ölçüm döngüsünü sonsuza kadar tetikleyebiliyor.
      const next = Math.round(
        snapToRow(
          node,
          Math.max(MIN_HEIGHT, available),
          rowSelector,
          headerSelector,
        ),
      );

      setMaxHeight((current) => (current === next ? current : next));
    };

    // Ölçüm zorunlu bir yeniden düzenleme (layout) tetikliyor. İki kaynak da aynı karede
    // ateşlenebildiği için tek bir kareye indiriliyor; yoksa pencere sürüklenirken her
    // karede iki tam ölçüm yapılıyordu.
    const schedule = () => {
      frame ||= requestAnimationFrame(measure);
    };

    measure();

    // Yalnız pencere olayını dinlemek yetmiyor: üstteki içerik değişince (uyarı kartı açılıyor,
    // kartlar satır atlıyor, veri gelip iskelet kalkıyor) kutu yer değiştiriyor ama pencere hiç
    // yeniden boyutlanmıyor.
    const observer = new ResizeObserver(schedule);

    observer.observe(document.body);
    window.addEventListener("resize", schedule);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [node, rowSelector, headerSelector]);

  return { ref: setNode, maxHeight };
}
